// src/components/contracts/SignedDocumentUpload.jsx
import React, { useState, useRef } from 'react';
import { Upload, X, FileText, Image, Archive, File, CheckCircle, AlertCircle } from 'lucide-react';

/**
 * Componentă generică pentru încărcarea documentelor semnate
 * Funcționează pentru: Contracte, Acte Adiționale, Notificări de Reziliere
 * 
 * @param {string} token - Token-ul de acces
 * @param {Function} onUploadSuccess - Callback la succes
 * @param {string} uploadType - Tipul documentului: 'contract' | 'additional-act' | 'notice'
 * @param {number} entityId - ID-ul entității (actId sau noticeId) - opțional pentru 'contract'
 * @param {string} entityLabel - Label pentru entitate (ex: "Act Adițional 1", "Reziliere #123")
 */
const SignedDocumentUpload = ({ 
  token, 
  onUploadSuccess, 
  uploadType = 'contract',
  entityId = null,
  entityLabel = 'document'
}) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadResponse, setUploadResponse] = useState(null);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const allowedExtensions = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.zip', '.7z', '.rar'];
  const maxFiles = 25;
  const maxFileSizeMB = 10;

  const getFileIcon = (fileName) => {
    const ext = fileName.toLowerCase().split('.').pop();

    if (['jpg', 'jpeg', 'png'].includes(ext)) {
      return <Image className="w-6 h-6 text-blue-500" />;
    }
    if (['zip', '7z', 'rar'].includes(ext)) {
      return <Archive className="w-6 h-6 text-orange-500" />;
    }
    if (['pdf'].includes(ext)) {
      return <FileText className="w-6 h-6 text-red-500" />;
    }
    if (['doc', 'docx'].includes(ext)) {
      return <FileText className="w-6 h-6 text-blue-600" />;
    }
    return <File className="w-6 h-6 text-gray-500" />;
  };

  const validateFile = (file) => {
    const ext = '.' + file.name.toLowerCase().split('.').pop();

    if (!allowedExtensions.includes(ext)) {
      return `Tipul fișierului "${ext}" nu este permis`;
    }

    if (file.size > maxFileSizeMB * 1024 * 1024) {
      return `Fișierul "${file.name}" depășește ${maxFileSizeMB}MB`;
    }

    return null;
  };

  const handleFiles = (fileList) => {
    if (!fileList) return;

    setError(null);
    const newFiles = [];
    const errors = [];

    Array.from(fileList).forEach((file) => {
      const validationError = validateFile(file);

      if (validationError) {
        errors.push(validationError);
        return;
      }

      if (files.length + newFiles.length >= maxFiles) {
        errors.push(`Maximum ${maxFiles} fișiere permise`);
        return;
      }

      const id = `${Date.now()}-${Math.random()}`;

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFiles(prev => prev.map(f =>
            f.id === id ? { ...f, preview: e.target.result } : f
          ));
        };
        reader.readAsDataURL(file);
      }

      newFiles.push({ file, id });
    });

    if (errors.length > 0) {
      setError(errors.join('; '));
    }

    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    handleFiles(e.dataTransfer.files);
  };

  const removeFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
    setError(null);
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Vă rugăm să selectați cel puțin un fișier');
      return;
    }

    // Validare entity ID pentru acte și rezilieri
    if ((uploadType === 'additional-act' || uploadType === 'notice') && !entityId) {
      setError('ID-ul entității lipsește');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      console.log(`=== UPLOAD START (${uploadType}) ===`);
      console.log('Files to upload:', files.length);
      console.log('Entity ID:', entityId);
      
      // Import the service dynamically
      const { contractAccessService } = await import('../../services/contractAccessService');

      // Extract just the File objects
      const filesToUpload = files.map(f => f.file);

      let data;
      
      // Call appropriate service method based on upload type
      switch (uploadType) {
        case 'additional-act':
          data = await contractAccessService.uploadAdditionalActDocuments(token, entityId, filesToUpload);
          break;
        case 'notice':
          data = await contractAccessService.uploadNoticeDocuments(token, entityId, filesToUpload);
          break;
        case 'contract':
        default:
          data = await contractAccessService.uploadSignedDocuments(token, filesToUpload);
          break;
      }

      setUploadResponse(data);
      setUploadSuccess(true);

      // Call parent callback if provided
      if (onUploadSuccess) {
        onUploadSuccess(data);
      }
    } catch (err) {
      console.error(`=== UPLOAD ERROR (${uploadType}) ===`);
      console.error('Error object:', err);
      console.error('Response status:', err.response?.status);
      console.error('Response data:', err.response?.data);
      console.error('Error message:', err.message);

      // Extract a user-friendly error message
      let errorMessage = 'Eroare la încărcarea documentelor';

      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.Message) {
          errorMessage = err.response.data.Message;
        } else if (err.response.data.errors) {
          const validationErrors = Object.values(err.response.data.errors).flat();
          errorMessage = validationErrors.join('; ');
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleUploadMore = () => {
    setUploadSuccess(false);
    setUploadResponse(null);
    setFiles([]);
    setError(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Success State
  if (uploadSuccess && uploadResponse) {
    return (
      <div className="p-4 sm:p-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-green-900">
                Încărcare reușită!
              </h3>
              <p className="text-green-700">{uploadResponse.message}</p>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <p className="text-sm text-gray-600">
              <strong>Total fișiere:</strong> {uploadResponse.totalFiles}
            </p>
            {uploadResponse.uploadedFiles && uploadResponse.uploadedFiles.length > 0 && (
              <div className="bg-white rounded p-3 space-y-1 max-h-60 overflow-y-auto">
                {uploadResponse.uploadedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between text-sm py-1">
                    <span className="text-gray-700 flex items-center gap-2">
                      {getFileIcon(file.fileName)}
                      {file.fileName}
                    </span>
                    <span className="text-gray-500">{file.fileSizeMB} MB</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={handleUploadMore}
            className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            Încarcă mai multe documente
          </button>
        </div>
      </div>
    );
  }

  // Upload Form State
  return (
    <div className="p-4 sm:p-6">
      <div className="bg-white rounded-lg">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Încarcă documente semnate
          </h3>
          <p className="text-sm text-gray-600">
            {uploadType === 'additional-act' && `Pentru ${entityLabel}`}
            {uploadType === 'notice' && `Pentru ${entityLabel}`}
            {uploadType === 'contract' && 'Pentru contract'}
          </p>
        </div>

        {/* Upload Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-6 sm:p-8 text-center transition-colors ${
            dragActive
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-base sm:text-lg mb-2">
            Trage fișierele aici sau
          </p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm sm:text-base"
          >
            Selectează fișiere
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={allowedExtensions.join(',')}
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
          />
          <p className="text-xs sm:text-sm text-gray-500 mt-4">
            Tipuri acceptate: PDF, DOC, DOCX, JPG, PNG, ZIP, 7Z, RAR (max {maxFileSizeMB}MB per fișier)
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Files List */}
        {files.length > 0 && (
          <div className="mt-6 space-y-3">
            <h4 className="font-semibold text-gray-900 text-sm sm:text-base">
              Fișiere selectate ({files.length}/{maxFiles})
            </h4>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {files.map(({ file, id, preview }) => (
                <div
                  key={id}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {preview ? (
                    <img
                      src={preview}
                      alt={file.name}
                      className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded"
                    />
                  ) : (
                    getFileIcon(file.name)
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate text-sm sm:text-base">
                      {file.name}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(id)}
                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                    disabled={uploading}
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              ))}
            </div>

            {/* Upload Button */}
            <button
              onClick={handleUpload}
              disabled={uploading || files.length === 0}
              className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm sm:text-base"
            >
              {uploading ? 'Se încarcă...' : `Încarcă ${files.length} document(e)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignedDocumentUpload;